import { motion } from 'framer-motion';
import { CheckCircle, UserPlus, Clock } from '../icons';
import type { RecentActivity } from '../../lib/admin-dashboard';

interface RecentActivityFeedProps {
  activities: RecentActivity[];
}

function formatTimeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function RecentActivityFeed({ activities }: RecentActivityFeedProps) {
  if (activities.length === 0) {
    return (
      <div className="freud-card-elevated p-6">
        <h3 className="text-text-xl text-gray-80 mb-4">Recent Activity</h3>
        <p className="text-text-sm text-gray-40 text-center py-6">
          No recent activity
        </p>
      </div>
    );
  }

  return (
    <div className="freud-card-elevated p-6">
      <h3 className="text-text-xl text-gray-80 mb-4">Recent Activity</h3>
      <div className="space-y-1 max-h-[400px] overflow-y-auto">
        {activities.map((item, index) => (
          <motion.div
            key={`${item.type}-${item.id}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
            className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-brand-5 transition-colors"
          >
            <div className={`p-1.5 rounded-lg flex-shrink-0 ${
              item.type === 'assessment' ? 'bg-success-5' : 'bg-teal-10'
            }`}>
              {item.type === 'assessment' ? (
                <CheckCircle className="w-4 h-4 text-success-40" />
              ) : (
                <UserPlus className="w-4 h-4 text-teal-50" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-text-sm text-gray-80 font-medium truncate">
                {item.userName}
              </p>
              <p className="text-text-xs text-gray-40 truncate">
                {item.type === 'assessment' ? 'Completed assessment' : 'New registration'} -- {item.detail}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Clock className="w-3 h-3 text-gray-30" />
              <span className="text-text-xs text-gray-40">
                {formatTimeAgo(item.timestamp)}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
